"use client"

import { useState, useEffect, useCallback } from "react"
import { useQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import type { CharacterDraft } from "@/features/characters/store"
import type { Character } from "@/shared/types/character"
import { useUpdateCharacter } from "@/features/characters/hooks/mutations/use-character-mutations"
import { cn } from "@/shared/lib/utils"
import { Brain, RotateCw } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { createClient } from "@/shared/lib/supabase/client"
import { toast } from "sonner"
import Image from "next/image"
import { ImageWithFallback } from "@/shared/components/image-with-fallback"
import {
  ETHNICITY_OPTIONS,
  HAIR_STYLE_OPTIONS,
  HAIR_COLORS,
  EYE_COLORS,
  BODY_TYPE_OPTIONS,
  BREAST_SIZE_OPTIONS,
  BUTT_SIZE_OPTIONS,
  PERSONALITY_CHIP_DATA,
  OCCUPATION_OPTIONS,
  RELATIONSHIP_OPTIONS,
  HOBBY_OPTIONS,
  FETISH_OPTIONS,
  STYLES,
  VOICE_FALLBACK_OPTIONS,
  resolveImg,
  ImageCardGrid,
  ColorSwatchPicker,
} from "./appearance-step"
import type { GS, FemaleOnly } from "./appearance-step"
import { GenesisAuraBackground } from "./genesis-aura-background"
import { SignupGateBackground } from "./signup-gate-background"
import { getBringToLifeLabel } from "@/features/create/lib/bring-to-life-label"
import { STYLE_PREVIEW_VIDEOS } from "@/features/create/lib/wizard-preview-videos"
import { WizardPreviewVideo } from "./wizard-preview-video"

// ─── Types ────────────────────────────────────────────────────────────────────

type EditField =
  | "ethnicity"
  | "eyeColor"
  | "hairColor"
  | "hairStyle"
  | "bodyType"
  | "breastSize"
  | "buttSize"
  | "age"
  | "personality"
  | "occupation"
  | "hobby"
  | "relationship"
  | "fetish"
  | "style"
  | "voice"
  | null

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveLabel(
  value: string,
  options: ReadonlyArray<{ value: string; label?: string; name?: string }>
): string {
  const match = options.find((o) => o.value === value)
  return match?.label ?? match?.name ?? value
}

function joinReadable(items: Array<string | null | undefined>): string {
  const filtered = items.filter((item): item is string => Boolean(item?.trim()))
  if (filtered.length <= 1) return filtered[0] ?? ""
  if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`
  return `${filtered.slice(0, -1).join(", ")}, and ${filtered[filtered.length - 1]}`
}

function sentenceCase(value: string): string {
  const trimmed = value.trim()
  return trimmed ? `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}` : ""
}

function withArticle(value: string): string {
  return `${/^[aeiou]/i.test(value.trim()) ? "an" : "a"} ${value}`
}

function buildCharacterHeaderDescription({
  name,
  storedDescription,
  tagline,
  traits,
  relationship,
  occupation,
  hobby,
  ethnicity,
  hairStyle,
  hairColor,
  eyeColor,
  gender,
}: {
  name: string
  storedDescription?: string | null
  tagline?: string | null
  traits: string[]
  relationship?: string | null
  occupation?: string | null
  hobby?: string | null
  ethnicity?: string | null
  hairStyle?: string | null
  hairColor?: string | null
  eyeColor?: string | null
  gender?: string | null
}): string | null {
  const stored = storedDescription?.trim()
  if (stored && stored.length >= 140) return stored

  const opening = stored || tagline?.trim()
  const pronoun = gender === "male" ? "He" : gender === "female" ? "She" : "They"
  const traitPhrase = joinReadable(traits.slice(0, 4))
  const hairLabel =
    hairColor && hairStyle
      ? withArticle(`${hairColor.toLowerCase()} ${hairStyle.toLowerCase()}`)
      : hairStyle
        ? withArticle(hairStyle.toLowerCase())
        : hairColor
          ? `${hairColor.toLowerCase()} hair`
          : null
  const appearance = joinReadable([
    ethnicity ? `${withArticle(ethnicity)} look` : null,
    hairLabel,
    eyeColor ? `${eyeColor.toLowerCase()} eyes` : null,
  ])
  const lifeDetails = joinReadable([
    relationship ? `your ${relationship.toLowerCase()}` : null,
    occupation ? `a ${occupation.toLowerCase()}` : null,
    hobby ? `drawn to ${hobby.toLowerCase()}` : null,
  ])

  const sentences = [
    opening,
    traitPhrase
      ? `${name} carries a ${traitPhrase.toLowerCase()} energy${appearance ? `, with ${appearance}` : ""}.`
      : appearance
        ? `${name} has a distinctive presence, with ${appearance}.`
        : null,
    lifeDetails
      ? `${pronoun} feels personal from the first moment: ${lifeDetails}, with enough depth to make every conversation reveal another side.`
      : `${pronoun} feels personal from the first moment, with enough depth to make every conversation reveal another side.`,
  ]
    .filter((sentence): sentence is string => Boolean(sentence?.trim()))
    .map(sentenceCase)

  return Array.from(new Set(sentences)).join(" ")
}

function getAttrImageUrl(
  attr: string,
  value: string,
  gender: string,
  style: string
): string {
  const lookup: Record<string, ReadonlyArray<{ value: string; images: GS | FemaleOnly }>> = {
    ethnicity: ETHNICITY_OPTIONS,
    hairStyle: HAIR_STYLE_OPTIONS,
    bodyType: BODY_TYPE_OPTIONS,
    breastSize: BREAST_SIZE_OPTIONS,
    buttSize: BUTT_SIZE_OPTIONS,
  }
  const list = lookup[attr]
  if (!list) return ""
  const opt = list.find((o) => o.value === value)
  return opt ? resolveImg(opt.images, gender, style) : ""
}

function getColorHex(attr: string, value: string): string | null {
  if (attr === "eyeColor") return EYE_COLORS.find((c) => c.value === value)?.color ?? null
  if (attr === "hairColor") return HAIR_COLORS.find((c) => c.value === value)?.color ?? null
  return null
}

// ─── Mini-Editors for Popup ───────────────────────────────────────────────────

function AgeEditor({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [age, setAge] = useState(value)
  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-6xl font-black text-white tabular-nums tracking-tight">{age}</div>
      <div className="w-full px-2">
        <input
          type="range"
          min={18}
          max={99}
          value={age}
          onChange={(e) => setAge(Number(e.target.value))}
          className="w-full h-2 cursor-pointer"
        />
        <div className="flex justify-between mt-2 text-[10px] text-muted-foreground font-medium">
          <span>18</span>
          <span>99</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onSave(age)}
        className="px-8 py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors"
      >
        Confirm
      </button>
    </div>
  )
}

function PersonalityEditor({ selected, onSave }: { selected: string[]; onSave: (v: string[]) => void }) {
  const [local, setLocal] = useState<string[]>(selected)
  const toggle = (v: string) =>
    setLocal((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {PERSONALITY_CHIP_DATA.map((c) => {
          const active = local.includes(c.value)
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => toggle(c.value)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all",
                active
                  ? "bg-accent-blue/15 border-accent-blue/50 text-primary"
                  : "bg-card border-border/50 text-muted-foreground hover:border-border hover:text-foreground"
              )}
            >
              <span>{c.emoji}</span>
              <span>{c.label}</span>
            </button>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => onSave(local)}
        className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 transition-colors"
      >
        Save ({local.length} selected)
      </button>
    </div>
  )
}

function VoiceEditor({
  voices,
  selectedId,
  onSelect,
}: {
  voices: Array<{ id: string; name: string }>
  selectedId: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {voices.map((voice) => (
        <button
          key={voice.id}
          type="button"
          onClick={() => onSelect(voice.id)}
          className={cn(
            "flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left transition-all",
            selectedId === voice.id
              ? "bg-accent-blue/10 border-accent-blue/50 text-white"
              : "bg-card border-border/40 text-muted-foreground hover:border-border hover:text-foreground hover:bg-card/80"
          )}
        >
          <Icon name="voice" size={16} className="size-4 shrink-0 text-muted-foreground" />
          <span className="font-semibold text-xs truncate">{voice.name}</span>
          {selectedId === voice.id && (
            <div className="ml-auto size-4 rounded-full bg-accent-blue text-white flex items-center justify-center shrink-0">
              <svg className="size-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      ))}
    </div>
  )
}

function OptionGridEditor({
  options,
  selected,
  onSelect,
}: {
  options: ReadonlyArray<{ value: string; label: string; emoji: string }>
  selected: string | null | undefined
  onSelect: (v: string) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={cn(
            "flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left transition-all",
            selected === opt.value
              ? "bg-accent-blue/10 border-accent-blue/50 text-white"
              : "bg-card border-border/40 text-muted-foreground hover:border-border hover:text-foreground hover:bg-card/80"
          )}
        >
          <span className="text-lg leading-none">{opt.emoji}</span>
          <span className="font-semibold text-xs">{opt.label}</span>
          {selected === opt.value && (
            <div className="ml-auto size-4 rounded-full bg-accent-blue text-white flex items-center justify-center shrink-0">
              <svg className="size-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </button>
      ))}
    </div>
  )
}

function StyleEditor({
  selected,
  onSelect,
}: {
  selected: string
  onSelect: (v: string) => void
}) {
  const t = useTranslations("create.wizard")

  return (
    <div className="grid grid-cols-2 gap-3">
      {STYLES.map((s) => {
        const styleCopy =
          s.value === "anime"
            ? {
                label: t("styles.anime.label"),
                desc: t("styles.anime.desc"),
              }
            : {
                label: t("styles.realistic.label"),
                desc: t("styles.realistic.desc"),
              }
        const active = selected === s.value
        const videoSources = STYLE_PREVIEW_VIDEOS[s.value]
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onSelect(s.value)}
            className={cn(
              "relative rounded-xl overflow-hidden aspect-[3/4] border-2 transition-all",
              active
                ? "border-accent-blue/50 ring-2 ring-accent-blue/30"
                : "border-border/40 hover:border-border"
            )}
          >
            <WizardPreviewVideo
              mp4={videoSources.mp4}
              poster={videoSources.poster}
              label={styleCopy.label}
              playing={active || !selected}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
              <span className={cn("text-sm font-bold", active ? "text-accent-blue" : "text-white")}>{styleCopy.label}</span>
              <p className="text-[10px] text-white/50 mt-0.5">{styleCopy.desc}</p>
            </div>
            {active && (
              <div className="absolute top-2 right-2 size-5 rounded-full bg-accent-blue text-white flex items-center justify-center">
                <svg className="size-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ─── Edit Popup ───────────────────────────────────────────────────────────────

const POPUP_TITLES: Record<string, string> = {
  ethnicity: "Ethnicity",
  eyeColor: "Eye Color",
  hairColor: "Hair Color",
  hairStyle: "Hair Style",
  bodyType: "Body Type",
  breastSize: "Breast Size",
  buttSize: "Butt Size",
  age: "Age",
  personality: "Personality",
  occupation: "Occupation",
  hobby: "Hobby",
  relationship: "Relationship",
  fetish: "Fetish",
  style: "Style",
  voice: "Voice",
}

function EditPopup({
  field,
  draft,
  onClose,
  onUpdate,
  voiceList,
}: {
  field: EditField
  draft: CharacterDraft
  onClose: () => void
  onUpdate: (data: Partial<CharacterDraft>) => void
  voiceList: Array<{ id: string; name: string }>
}) {
  const gender = draft.gender || "female"
  const style = draft.style || "realistic"

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [onClose])

  if (!field) return null

  const close = onClose
  const save = (data: Partial<CharacterDraft>) => { onUpdate(data); close() }

  const renderContent = () => {
    switch (field) {
      case "ethnicity":
        return <ImageCardGrid options={ETHNICITY_OPTIONS} selected={draft.ethnicity} onSelect={(v) => save({ ethnicity: v })} gender={gender} style={style} columns={3} compact />
      case "hairStyle":
        return <ImageCardGrid options={HAIR_STYLE_OPTIONS} selected={draft.hairStyle} onSelect={(v) => save({ hairStyle: v })} gender={gender} style={style} columns={4} compact />
      case "bodyType":
        return <ImageCardGrid options={BODY_TYPE_OPTIONS} selected={draft.bodyType} onSelect={(v) => save({ bodyType: v })} gender={gender} style={style} columns={4} compact />
      case "breastSize":
        return <ImageCardGrid options={BREAST_SIZE_OPTIONS} selected={draft.breastSize} onSelect={(v) => save({ breastSize: v })} gender={gender} style={style} columns={4} compact />
      case "buttSize":
        return <ImageCardGrid options={BUTT_SIZE_OPTIONS} selected={draft.buttSize} onSelect={(v) => save({ buttSize: v })} gender={gender} style={style} columns={4} compact />
      case "eyeColor":
        return <ColorSwatchPicker options={EYE_COLORS} selected={draft.eyeColor} onSelect={(v) => save({ eyeColor: v })} />
      case "hairColor":
        return <ColorSwatchPicker options={HAIR_COLORS} selected={draft.hairColor} onSelect={(v) => save({ hairColor: v })} />
      case "age":
        return <AgeEditor value={draft.age} onSave={(v) => save({ age: v })} />
      case "personality":
        return <PersonalityEditor selected={draft.personality as string[]} onSave={(v) => save({ personality: v as unknown as CharacterDraft["personality"] })} />
      case "occupation":
        return <OptionGridEditor options={OCCUPATION_OPTIONS} selected={draft.occupation} onSelect={(v) => save({ occupation: v })} />
      case "hobby":
        return <OptionGridEditor options={HOBBY_OPTIONS} selected={draft.hobby} onSelect={(v) => save({ hobby: v })} />
      case "relationship":
        return <OptionGridEditor options={RELATIONSHIP_OPTIONS} selected={draft.relationshipType} onSelect={(v) => save({ relationshipType: v })} />
      case "fetish":
        return <OptionGridEditor options={FETISH_OPTIONS} selected={draft.fetish} onSelect={(v) => save({ fetish: v })} />
      case "style":
        return <StyleEditor selected={draft.style || "realistic"} onSelect={(v) => save({ style: v as "realistic" | "anime" })} />
      case "voice":
        return (
          <VoiceEditor
            voices={voiceList}
            selectedId={draft.voiceId}
            onSelect={(id) => save({ voiceId: id })}
          />
        )
      default:
        return null
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
    >
      <div className="w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-2xl max-h-[75vh] sm:max-h-[85vh] flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200">
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/50 shrink-0">
          <h3 className="text-sm font-bold text-foreground">{POPUP_TITLES[field]}</h3>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="size-7 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          >
            <Icon name="x" size={14} className="size-3.5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-5 flex-1">{renderContent()}</div>
      </div>
    </div>
  )
}

// ─── Attribute Cards ──────────────────────────────────────────────────────────

function ImageAttrCard({
  label,
  value,
  imageUrl,
  onEdit,
}: {
  label: string
  value: string | null | undefined
  imageUrl: string
  onEdit: () => void
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group relative h-[92px] overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(135deg,rgba(20,30,42,0.96),rgba(8,12,18,0.98))] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_34px_rgba(0,0,0,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/18 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_18px_45px_rgba(0,0,0,0.28)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-[116px]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(85%_110%_at_15%_15%,rgba(255,255,255,0.07),transparent_55%)]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/18 to-transparent" />

      {imageUrl && (
        <div className="absolute inset-y-0 right-0 w-[58%] overflow-hidden">
          <Image
            src={imageUrl}
            alt=""
            fill
            className="object-cover opacity-70 saturate-[0.95] transition duration-500 group-hover:scale-105 group-hover:opacity-85"
            sizes="(max-width: 640px) 28vw, 160px"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-white/5" />
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-white/0 transition-colors duration-300 group-hover:bg-white/[0.03]" />

      {/* Edit chevron */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="size-6 rounded-full bg-black/45 backdrop-blur flex items-center justify-center border border-white/12">
          <Icon name="arrow-right-2" size={10} className="size-2.5 text-white" />
        </div>
      </div>

      {/* Label & value */}
      <div className="absolute inset-x-0 bottom-0 px-3.5 py-3">
        <span className="block text-[9px] uppercase tracking-[0.14em] font-black text-white/42 mb-1">{label}</span>
        <span className={cn("block text-[14px] font-black leading-tight", value ? "text-white" : "text-white/25 italic text-[11px]")}>
          {value ?? "Not set"}
        </span>
      </div>
    </button>
  )
}

function ColorAttrCard({
  label,
  value,
  colorHex,
  onEdit,
}: {
  label: string
  value: string | null | undefined
  colorHex: string | null
  onEdit: () => void
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group relative h-[92px] overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(135deg,rgba(20,30,42,0.96),rgba(8,12,18,0.98))] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_34px_rgba(0,0,0,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/18 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-[116px]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(85%_110%_at_15%_15%,rgba(255,255,255,0.07),transparent_55%)]" />
      {colorHex && (
        <>
          <div
            className="absolute right-2 top-1/2 h-28 w-28 -translate-y-1/2 rounded-full blur-[34px] opacity-25 pointer-events-none"
            style={{ background: colorHex }}
          />
          <div
            className="absolute top-4 right-8 size-11 rounded-full border border-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_10px_28px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-105"
            style={{ background: colorHex }}
          />
        </>
      )}

      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="size-6 rounded-full bg-black/45 backdrop-blur flex items-center justify-center border border-white/12">
          <Icon name="arrow-right-2" size={10} className="size-2.5 text-white" />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-3.5 py-3">
        <span className="block text-[9px] uppercase tracking-[0.14em] font-black text-muted-foreground mb-1">{label}</span>
        <span className={cn("block text-[14px] font-black leading-tight", value ? "text-foreground" : "text-muted-foreground/30 italic text-[11px]")}>
          {value ?? "Not set"}
        </span>
      </div>
    </button>
  )
}

function SimpleAttrCard({
  label,
  value,
  display,
  onEdit,
}: {
  label: string
  value: string | number | null | undefined
  display?: React.ReactNode
  onEdit: () => void
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="group relative h-[92px] overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(135deg,rgba(20,30,42,0.96),rgba(8,12,18,0.98))] text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_34px_rgba(0,0,0,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:border-white/18 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-[116px]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(85%_110%_at_15%_15%,rgba(255,255,255,0.07),transparent_55%)]" />
      {display && (
        <div className="absolute inset-0 flex items-center justify-end pr-8 pb-6 pointer-events-none opacity-80">
          {display}
        </div>
      )}

      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="size-6 rounded-full bg-black/45 backdrop-blur flex items-center justify-center border border-white/12">
          <Icon name="arrow-right-2" size={10} className="size-2.5 text-white" />
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-3.5 py-3">
        <span className="block text-[9px] uppercase tracking-[0.14em] font-black text-muted-foreground mb-1">{label}</span>
        <span className={cn("block text-[14px] font-black leading-tight", value ? "text-foreground" : "text-muted-foreground/30 italic text-[11px]")}>
          {value != null ? String(value) : "Not set"}
        </span>
      </div>
    </button>
  )
}

// ─── Left Preview Panel ────────────────────────────────────────────────────────

interface PreviewPanelProps {
  draft: CharacterDraft
  isCreating: boolean
  showSignupCTA: boolean
  onSignupClick?: () => void
  onChatClick?: () => void
  simulatedProgress: number
  creationPhase: string
  createdCharacter?: Character
  finalizeStalled?: boolean
  onRetryFinalize?: () => void
}

function PreviewPanel({
  draft,
  isCreating,
  showSignupCTA,
  onSignupClick,
  onChatClick,
  simulatedProgress,
  creationPhase,
  createdCharacter,
  finalizeStalled = false,
  onRetryFinalize,
}: PreviewPanelProps) {
  const progressRadius = 50
  const progressCircumference = 2 * Math.PI * progressRadius
  const pct = isCreating ? simulatedProgress : createdCharacter ? 100 : 0
  const progressDashOffset = progressCircumference * (1 - pct / 100)
  const createdAvatarSrc = createdCharacter?.avatar ?? null
  const [revealedAvatarSrc, setRevealedAvatarSrc] = useState<string | null>(null)
  const createdAvatarReady =
    createdAvatarSrc !== null && revealedAvatarSrc === createdAvatarSrc

  // Created state — show the generated character image
  if (createdCharacter?.avatar) {
    return (
      <div className="relative -mx-2 h-[min(82dvh,760px)] min-h-[620px] overflow-hidden bg-black shadow-none sm:min-h-[640px] md:-mx-0 md:h-auto md:min-h-[540px] md:rounded-[6px] md:border md:border-white/[0.07] md:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_80px_rgba(0,0,0,0.34)]">
        <ImageWithFallback
          src={createdCharacter.avatar}
          alt={createdCharacter.name}
          fill
          className={cn(
            "object-cover object-top md:object-center",
            createdAvatarReady && "animate-in zoom-in-95 duration-700"
          )}
          sizes="(max-width: 768px) 100vw, 50vw"
          priority
          onLoad={() => setRevealedAvatarSrc(createdCharacter.avatar)}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0)_42%,rgba(2,8,14,0.30)_58%,rgba(0,0,0,0.82)_80%,#000_96%,#000_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(90%_46%_at_50%_70%,rgba(16,58,90,0.34)_0%,rgba(4,20,32,0.22)_42%,rgba(0,0,0,0)_74%)]" />
        <div className="absolute inset-x-0 bottom-0 h-[24%] bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,#000_86%,#000_100%)]" />
        {onChatClick && (
          <div className="absolute bottom-0 left-0 right-0 hidden px-5 pb-5 pt-24 sm:block">
            <button
              type="button"
              onClick={onChatClick}
              className="group relative flex w-full items-center justify-center overflow-hidden rounded-full border border-white/18 bg-accent-blue px-5 py-3 text-sm font-extrabold text-white shadow-[0_16px_42px_rgba(29,125,242,0.38),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-14px_24px_rgba(0,24,84,0.24)] transition hover:brightness-110 active:scale-[0.98]"
            >
              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0.08)_38%,rgba(0,0,0,0.18)_100%)] opacity-95 transition-opacity group-hover:opacity-100" />
              <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/60" />
              <span className="relative z-10">{getBringToLifeLabel(createdCharacter.gender)}</span>
            </button>
          </div>
        )}
      </div>
    )
  }

  if (finalizeStalled) {
    const stalledName = draft.name?.trim() || "Your companion"
    return (
      <div className="relative rounded-[6px] overflow-hidden flex flex-col min-h-[300px] sm:min-h-[380px] md:min-h-[540px] border border-[rgba(168,178,198,0.24)] bg-black">
        <SignupGateBackground />

        <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">
            Taking longer than expected
          </p>
          <h2 className="heading-serif text-base sm:text-lg md:text-xl text-white leading-snug">
            <span className="text-primary">{stalledName}</span> is almost ready
          </h2>
          <p className="max-w-[280px] text-sm leading-relaxed text-white/55">
            We&apos;re still loading the final details. Try again in a moment.
          </p>
          {onRetryFinalize && (
            <button
              type="button"
              onClick={onRetryFinalize}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-8 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:border-primary hover:bg-primary/20 active:scale-[0.98]"
            >
              <RotateCw className="size-4" />
              Try again
            </button>
          )}
        </div>
      </div>
    )
  }

  // Signup gate — loading is done; CTA only (no progress ring)
  if (showSignupCTA) {
    const fallbackSubject = draft.gender === "male" ? "He" : draft.gender === "trans" ? "They" : "She"
    const objectPronoun = draft.gender === "male" ? "him" : draft.gender === "trans" ? "them" : "her"
    const characterLabel = draft.name?.trim() || fallbackSubject
    const previewGender = draft.gender || "female"
    const previewStyle = draft.style || "realistic"
    const revealImages = [
      draft.ethnicity ? getAttrImageUrl("ethnicity", draft.ethnicity, previewGender, previewStyle) : "",
      draft.hairStyle ? getAttrImageUrl("hairStyle", draft.hairStyle, previewGender, previewStyle) : "",
      draft.bodyType ? getAttrImageUrl("bodyType", draft.bodyType, previewGender, previewStyle) : "",
    ].filter((src): src is string => Boolean(src)).slice(0, 3)
    const revealSlots = [revealImages[0] ?? "", revealImages[1] ?? "", revealImages[2] ?? ""]

    return (
      <div className="relative overflow-hidden rounded-[6px] border border-white/[0.07] bg-black shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_80px_rgba(0,0,0,0.34)] flex flex-col min-h-[380px] sm:min-h-[420px] md:min-h-[540px]">
        <SignupGateBackground />

        <div className="relative z-10 flex flex-col flex-1 px-5 pb-5 sm:px-6 sm:pb-8">
          <div className="pt-6 sm:pt-8 text-center shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/40">
              One step left
            </p>
            <h2 className="heading-serif mt-2 text-base sm:text-lg md:text-xl text-white leading-snug">
              <span className="text-primary">{characterLabel}</span> is almost here
            </h2>
          </div>

          {/* Mobile CTA lives in the wizard fixed bottom bar; desktop keeps it in-card. */}
          <div className="flex flex-1 flex-col items-center justify-center text-center mt-5 md:mt-0">
            <p className="text-sm text-white/55 max-w-[280px] leading-relaxed md:mb-6">
              Something&apos;s taking shape. Sign in to finish and meet {objectPronoun}.
            </p>
            <div className="mt-5 w-full max-w-[20rem] md:hidden" data-signup-reveal-preview>
              <div className="relative h-[132px] overflow-hidden rounded-2xl border border-white/10 bg-black/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_18px_54px_rgba(0,0,0,0.4)] backdrop-blur-md">
                <div className="absolute inset-0 bg-[radial-gradient(90%_120%_at_50%_0%,rgba(29,125,242,0.22),rgba(29,125,242,0.08)_42%,rgba(0,0,0,0)_74%)]" />
                <div className="absolute inset-3 grid grid-cols-3 gap-2">
                  {revealSlots.map((src, index) => (
                    <div
                      key={`${src || "preview"}-${index}`}
                      className={cn(
                        "relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.035]",
                        index === 1 ? "translate-y-[-6px]" : "translate-y-2"
                      )}
                    >
                      {src ? (
                        <Image
                          src={src}
                          alt=""
                          fill
                          sizes="100px"
                          className="scale-110 object-cover opacity-55 blur-[1.5px]"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-[linear-gradient(145deg,rgba(29,125,242,0.18),rgba(255,255,255,0.05)_42%,rgba(0,0,0,0.08))]" />
                      )}
                      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.1)_0%,rgba(0,0,0,0.22)_45%,rgba(0,0,0,0.72)_100%)]" />
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.48),rgba(0,0,0,0.02)_44%,rgba(0,0,0,0.48))]" />
                <div className="absolute inset-x-0 bottom-0 h-20 bg-[linear-gradient(180deg,rgba(0,0,0,0),rgba(0,8,18,0.78))]" />
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
                  <span className="flex size-12 items-center justify-center rounded-full border border-white/18 bg-black/45 text-white shadow-[0_0_34px_rgba(29,125,242,0.34),inset_0_1px_0_rgba(255,255,255,0.18)] backdrop-blur-md">
                    <Icon name="lock" size={17} className="size-4" />
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
                  <span className="h-1.5 w-5 rounded-full bg-accent-blue shadow-[0_0_14px_rgba(29,125,242,0.55)]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/28" />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/18" />
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onSignupClick}
              className="group relative hidden min-w-[15rem] items-center justify-center overflow-hidden rounded-full border border-white/18 bg-accent-blue px-10 py-3.5 text-sm font-extrabold text-white shadow-[0_16px_42px_rgba(29,125,242,0.38),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-14px_24px_rgba(0,24,84,0.24)] transition-all hover:brightness-110 active:scale-[0.98] md:inline-flex"
            >
              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0.08)_38%,rgba(0,0,0,0.18)_100%)] opacity-95 transition-opacity group-hover:opacity-100" />
              <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/60" />
              <span className="relative z-10">Unlock &amp; create free</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Creating state — loader card (left column): warm reveal background + progress ring
  if (isCreating) {
    return (
      <div className="relative overflow-hidden rounded-[6px] border border-white/[0.07] bg-black shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_80px_rgba(0,0,0,0.34)] flex flex-col min-h-[300px] sm:min-h-[380px] md:min-h-[540px]">
        <SignupGateBackground />

        <div className="relative z-10 flex flex-col flex-1 px-6">
          <h2 className="heading-serif pt-6 sm:pt-8 text-center text-base sm:text-lg md:text-xl text-white leading-snug">
            <span className="text-primary">Your Dream Companion</span> is waiting
          </h2>

          <div className="flex flex-1 items-center justify-center py-5">
            <div
              className="relative flex size-[132px] items-center justify-center rounded-full sm:size-[148px]"
              role="progressbar"
              aria-label="Creation progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
            >
              <div className="absolute inset-0 rounded-full bg-black/25 shadow-[0_0_30px_color-mix(in_srgb,var(--accent-blue)_20%,transparent)]" />
              <div className="absolute inset-[12px] rounded-full bg-[radial-gradient(circle_at_50%_42%,rgba(255,255,255,0.09),rgba(0,0,0,0.58)_48%,rgba(0,0,0,0.86)_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),inset_0_-18px_32px_rgba(0,0,0,0.32)] backdrop-blur-md" />
              <svg className="absolute inset-0 size-full -rotate-90 overflow-visible" viewBox="0 0 120 120">
                <defs>
                  <linearGradient id="creation-progress-gradient" x1="20" y1="96" x2="98" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="color-mix(in srgb, var(--primary) 72%, white)" />
                    <stop offset="0.58" stopColor="color-mix(in srgb, var(--accent-blue) 88%, white)" />
                    <stop offset="1" stopColor="white" />
                  </linearGradient>
                </defs>
                <circle
                  cx="60"
                  cy="60"
                  r={progressRadius}
                  fill="none"
                  stroke="rgba(255,255,255,0.09)"
                  strokeWidth="5.5"
                />
                <circle
                  cx="60"
                  cy="60"
                  r={progressRadius}
                  fill="none"
                  stroke="url(#creation-progress-gradient)"
                  strokeWidth="5.5"
                  strokeLinecap="round"
                  strokeDasharray={progressCircumference}
                  strokeDashoffset={progressDashOffset}
                  className="transition-all duration-500 ease-out"
                  style={{ filter: "drop-shadow(0 0 8px color-mix(in srgb, var(--accent-blue) 46%, transparent))" }}
                />
              </svg>
              <div className="relative z-10 flex items-center justify-center">
                <span className="text-[2rem] font-black leading-none text-white tabular-nums drop-shadow-[0_0_16px_rgba(255,255,255,0.22)] sm:text-[2.35rem]">
                  {pct}%
                </span>
              </div>
            </div>
          </div>

          <p className="pb-6 sm:pb-8 text-center text-sm font-medium text-white/80">
            {creationPhase || "Creating your AI"}
          </p>
        </div>
      </div>
    )
  }

  // Review state — abstract genesis teaser (character stays hidden until created)
  return (
    <div className="relative overflow-hidden rounded-[6px] border border-white/[0.07] bg-black shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_80px_rgba(0,0,0,0.34)] flex flex-col items-center justify-end min-h-[300px] sm:min-h-[380px] md:min-h-[540px]">
      <GenesisAuraBackground variant="idle" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 py-7 w-full">
        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] text-white/40">Almost there</p>
        <h2 className="heading-serif mt-1.5 text-xl sm:text-2xl md:text-3xl text-white leading-tight">
          Your dream companion <span className="text-accent-blue">awaits</span>
        </h2>
        <p className="mt-2 text-xs sm:text-sm text-white/55">
          Review the details, then tap <span className="text-white font-semibold">Generate</span>
        </p>
      </div>
    </div>
  )
}

// ─── Character Tab Section ─────────────────────────────────────────────────────

// Flat attribute card (Character tab) — label over value, optional emoji. Tappable
// to edit when onEdit is provided.
function FlatCard({
  label,
  value,
  emoji,
  onEdit,
  className,
}: {
  label: string
  value: string
  emoji?: string
  onEdit?: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onEdit}
      disabled={!onEdit}
      className={cn(
        "group flex flex-col gap-1 rounded-[6px] border border-border/40 bg-foreground/[0.03] px-3 py-2.5 text-left transition-colors hover:border-border/70 disabled:pointer-events-none disabled:cursor-default min-h-[60px]",
        className
      )}
    >
      <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 text-sm font-bold text-foreground">
        {emoji && <span className="text-base leading-none shrink-0">{emoji}</span>}
        <span className="truncate">{value}</span>
      </span>
    </button>
  )
}

// Long-form detail block (Character tab).
function DetailBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[6px] border border-border/40 bg-foreground/[0.03] px-3 py-2.5">
      <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────

interface ReviewStepProps {
  isCreating: boolean
  showSignupCTA?: boolean
  onSignupClick?: () => void
  onChatClick?: () => void
  simulatedProgress: number
  creationPhase: string
  createdCharacter?: Character
  finalizeStalled?: boolean
  onRetryFinalize?: () => void
}

export function ReviewStep({
  isCreating,
  showSignupCTA = false,
  onSignupClick,
  onChatClick,
  simulatedProgress,
  creationPhase,
  createdCharacter,
  finalizeStalled = false,
  onRetryFinalize,
}: ReviewStepProps) {
  const { draft, updateDraft } = useCharacterStore()
  const [activeTab, setActiveTab] = useState<"appearance" | "character">("appearance")
  const [editingField, setEditingField] = useState<EditField>(null)
  const [isEditingCreatedName, setIsEditingCreatedName] = useState(false)
  const [createdNameDraft, setCreatedNameDraft] = useState("")
  const updateCharacterMutation = useUpdateCharacter()
  const readOnly = isCreating || showSignupCTA

  useEffect(() => {
    if (isCreating || showSignupCTA) setActiveTab("appearance")
  }, [isCreating, showSignupCTA])

  const { data: voices } = useQuery({
    queryKey: ["ai_assets", "tts_voice", "active"],
    queryFn: async () => {
      const supabase = createClient()
      const { data } = await supabase
        .from("ai_assets")
        .select("id, name, description, thumbnail_url, type_config")
        .eq("asset_type", "tts_voice")
        .eq("status", "active")
        .order("name")
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  const voiceList: Array<{ id: string; name: string }> =
    voices && voices.length > 0
      ? voices.map((v) => ({ id: v.id, name: v.name }))
      : VOICE_FALLBACK_OPTIONS.map((v) => ({ id: v.id, name: v.name }))

  const selectedVoiceName = voiceList.find((v) => v.id === draft.voiceId)?.name ?? null

  const handleUpdate = useCallback(
    (data: Partial<CharacterDraft>) => updateDraft(data),
    [updateDraft]
  )

  useEffect(() => {
    if (createdCharacter?.name) {
      setCreatedNameDraft(createdCharacter.name)
      setIsEditingCreatedName(false)
    }
  }, [createdCharacter?.name])

  const saveCreatedName = useCallback(async () => {
    if (!createdCharacter) return

    const trimmedName = createdNameDraft.trim()
    if (trimmedName.length < 2) {
      toast.error("Please enter a name with at least 2 characters")
      return
    }

    if (trimmedName === createdCharacter.name) {
      setIsEditingCreatedName(false)
      return
    }

    try {
      const result = await updateCharacterMutation.mutateAsync({
        id: createdCharacter.id,
        data: { name: trimmedName },
      })

      if (!result.success) {
        toast.error(result.error || "Failed to update character name")
        return
      }

      toast.success("Character name updated")
      setIsEditingCreatedName(false)
    } catch {
      toast.error("Failed to update character name. Please try again.")
    }
  }, [createdCharacter, createdNameDraft, updateCharacterMutation])

  const gender = draft.gender || "female"
  const style = draft.style || "realistic"
  const isCharacterCreated = !!createdCharacter
  const summaryEditingDisabled = readOnly || isCharacterCreated
  const displayName = createdCharacter?.name || draft.name?.trim() || "Your Character"

  const personalityChips = (draft.personality as string[])
    .map((p) => PERSONALITY_CHIP_DATA.find((c) => c.value === p))
    .filter(Boolean) as Array<{ value: string; emoji: string; label: string }>

  const relationshipOpt = RELATIONSHIP_OPTIONS.find((o) => o.value === draft.relationshipType)
  const relationshipLabel =
    draft.relationshipType && draft.relationshipType !== "none"
      ? relationshipOpt?.label ?? draft.relationshipType
      : null

  const occupationOpt = OCCUPATION_OPTIONS.find((o) => o.value === draft.occupation)
  const hobbyOpt = HOBBY_OPTIONS.find((o) => o.value === draft.hobby)
  const fetishOpt = FETISH_OPTIONS.find((o) => o.value === draft.fetish)
  const occupationVal = draft.occupation && draft.occupation !== "none" ? occupationOpt?.label ?? draft.occupation : null
  const hobbyVal = draft.hobby && draft.hobby !== "none" ? hobbyOpt?.label ?? draft.hobby : null
  const fetishVal = draft.fetish && draft.fetish !== "none" ? fetishOpt?.label ?? draft.fetish : null
  const personalityLabel = personalityChips.map((c) => c.label).join(", ")
  const ethnicityLabel = draft.ethnicity
    ? resolveLabel(draft.ethnicity, ETHNICITY_OPTIONS)
    : createdCharacter?.ethnicity ?? null
  const hairStyleLabel = draft.hairStyle
    ? resolveLabel(draft.hairStyle, HAIR_STYLE_OPTIONS)
    : createdCharacter?.hairStyle ?? null
  const hairColorLabel = draft.hairColor
    ? resolveLabel(draft.hairColor, HAIR_COLORS)
    : createdCharacter?.hairColor ?? null
  const eyeColorLabel = draft.eyeColor
    ? resolveLabel(draft.eyeColor, EYE_COLORS)
    : createdCharacter?.eyeColor ?? null
  const headerDescription = isCharacterCreated
    ? buildCharacterHeaderDescription({
        name: displayName,
        storedDescription: createdCharacter?.description,
        tagline: createdCharacter?.tagline,
        traits: personalityChips.map((c) => c.label),
        relationship: relationshipLabel,
        occupation: occupationVal,
        hobby: hobbyVal,
        ethnicity: ethnicityLabel,
        hairStyle: hairStyleLabel,
        hairColor: hairColorLabel,
        eyeColor: eyeColorLabel,
        gender: createdCharacter?.gender ?? draft.gender,
      })
    : null

  return (
    <>
      {editingField && !readOnly && !isCharacterCreated && (
        <EditPopup
          field={editingField}
          draft={draft}
          onClose={() => setEditingField(null)}
          onUpdate={handleUpdate}
          voiceList={voiceList}
        />
      )}

      <div className="grid gap-4 sm:gap-5 grid-cols-1 md:grid-cols-[1fr_1.15fr]">

        {/* ── Left: Preview / loading ── */}
        <PreviewPanel
          draft={draft}
          isCreating={isCreating}
          showSignupCTA={showSignupCTA}
          onSignupClick={onSignupClick}
          onChatClick={onChatClick}
          simulatedProgress={simulatedProgress}
          creationPhase={creationPhase}
          createdCharacter={createdCharacter}
          finalizeStalled={finalizeStalled}
          onRetryFinalize={onRetryFinalize}
        />

        {/* ── Right: character summary (visible during generation, review, and result) ── */}
        <div className="flex flex-col gap-3">

          {/* Premium header: name + age, relationship + voice */}
          <div className="flex flex-col gap-2.5">
            {isCharacterCreated && isEditingCreatedName ? (
              <div className="flex items-center gap-2">
                <input
                  value={createdNameDraft}
                  onChange={(e) => setCreatedNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveCreatedName()
                    if (e.key === "Escape") {
                      setCreatedNameDraft(createdCharacter?.name ?? "")
                      setIsEditingCreatedName(false)
                    }
                  }}
                  className="min-w-0 flex-1 rounded-full border border-border/50 bg-card px-4 py-2 text-lg font-black text-foreground outline-none focus:border-border"
                  autoFocus
                  maxLength={40}
                />
                <button
                  type="button"
                  onClick={() => void saveCreatedName()}
                  disabled={updateCharacterMutation.isPending}
                  className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                >
                  {updateCharacterMutation.isPending ? "Saving" : "Save"}
                </button>
              </div>
            ) : (
              <div className="flex items-baseline gap-1.5">
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground leading-none">
                  {displayName}
                </h2>
                {isCharacterCreated && (
                  <button
                    type="button"
                    onClick={() => setIsEditingCreatedName(true)}
                    aria-label="Edit character name"
                    className="mb-0.5 ml-0.5 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                  >
                    <Icon name="edit" size={16} className="size-4" />
                  </button>
                )}
                {draft.age != null && (
                  <span className="text-xl sm:text-2xl font-bold text-muted-foreground leading-none tabular-nums">
                    , {draft.age}
                  </span>
                )}
              </div>
            )}
            {headerDescription && (
              <p className="max-w-[44rem] text-[13px] leading-relaxed text-muted-foreground/85 line-clamp-4 sm:text-sm sm:leading-relaxed">
                {headerDescription}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {relationshipLabel && (
                summaryEditingDisabled ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-card/60 px-3 py-1.5 text-xs font-semibold text-foreground/80">
                    {relationshipOpt?.emoji && <span className="leading-none">{relationshipOpt.emoji}</span>}
                    {relationshipLabel}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingField("relationship")}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-card/60 px-3 py-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:border-border hover:text-foreground"
                  >
                    {relationshipOpt?.emoji && <span className="leading-none">{relationshipOpt.emoji}</span>}
                    {relationshipLabel}
                  </button>
                )
              )}
              <span
                aria-label={selectedVoiceName ? `Voice: ${selectedVoiceName}` : "Voice"}
                className="flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary"
              >
                <Icon name="voice" size={14} className="size-3.5" />
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex rounded-xl bg-card/80 border border-border/30 p-0.5">
            {(["appearance", "character"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-[10px] text-xs sm:text-sm font-bold transition-all",
                  activeTab === tab
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {tab === "appearance" ? (
                  <>
                    <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Appearance
                  </>
                ) : (
                  <>
                    <Brain className="size-3.5" />
                    Character
                  </>
                )}
              </button>
            ))}
          </div>

          {/* ── Appearance Tab ── */}
          {activeTab === "appearance" && (
            <div className={cn("grid grid-cols-2 gap-1.5 sm:gap-2", summaryEditingDisabled && "pointer-events-none")}>
              <ImageAttrCard
                label="Ethnicity"
                value={draft.ethnicity ? resolveLabel(draft.ethnicity, ETHNICITY_OPTIONS) : null}
                imageUrl={draft.ethnicity ? getAttrImageUrl("ethnicity", draft.ethnicity, gender, style) : ""}
                onEdit={() => setEditingField("ethnicity")}
              />
              <SimpleAttrCard
                label="Age"
                value={draft.age != null ? `${draft.age}yo` : null}
                display={
                  <span className="text-4xl font-black text-foreground/10 group-hover:text-foreground/15 transition-colors">{draft.age ?? 21}</span>
                }
                onEdit={() => setEditingField("age")}
              />
              <ColorAttrCard
                label="Eye Color"
                value={draft.eyeColor || null}
                colorHex={draft.eyeColor ? getColorHex("eyeColor", draft.eyeColor) : null}
                onEdit={() => setEditingField("eyeColor")}
              />
              <ImageAttrCard
                label="Body Type"
                value={draft.bodyType ? resolveLabel(draft.bodyType, BODY_TYPE_OPTIONS) : null}
                imageUrl={draft.bodyType ? getAttrImageUrl("bodyType", draft.bodyType, gender, style) : ""}
                onEdit={() => setEditingField("bodyType")}
              />
              <ImageAttrCard
                label="Breast Size"
                value={draft.breastSize ? resolveLabel(draft.breastSize, BREAST_SIZE_OPTIONS) : null}
                imageUrl={draft.breastSize ? getAttrImageUrl("breastSize", draft.breastSize, gender, style) : ""}
                onEdit={() => setEditingField("breastSize")}
              />
              <ImageAttrCard
                label="Hair Style"
                value={draft.hairStyle ? resolveLabel(draft.hairStyle, HAIR_STYLE_OPTIONS) : null}
                imageUrl={draft.hairStyle ? getAttrImageUrl("hairStyle", draft.hairStyle, gender, style) : ""}
                onEdit={() => setEditingField("hairStyle")}
              />
              <ColorAttrCard
                label="Hair Color"
                value={draft.hairColor || null}
                colorHex={draft.hairColor ? getColorHex("hairColor", draft.hairColor) : null}
                onEdit={() => setEditingField("hairColor")}
              />
              <ImageAttrCard
                label="Butt Size"
                value={draft.buttSize ? resolveLabel(draft.buttSize, BUTT_SIZE_OPTIONS) : null}
                imageUrl={draft.buttSize ? getAttrImageUrl("buttSize", draft.buttSize, gender, style) : ""}
                onEdit={() => setEditingField("buttSize")}
              />
              {/* Style card */}
              <SimpleAttrCard
                label="Style"
                value={draft.style ? draft.style.charAt(0).toUpperCase() + draft.style.slice(1) : null}
                display={
                  <span className="text-2xl opacity-10 group-hover:opacity-20 transition-opacity">
                    {draft.style === "anime" ? "✨" : "📸"}
                  </span>
                }
                onEdit={() => setEditingField("style")}
              />
              {/* Fetish (optional) */}
              {draft.fetish && draft.fetish !== "none" && (
                <SimpleAttrCard
                  label="Fetish"
                  value={resolveLabel(draft.fetish, FETISH_OPTIONS)}
                  display={
                    <span className="text-3xl opacity-15 group-hover:opacity-25 transition-opacity">
                      {FETISH_OPTIONS.find((f) => f.value === draft.fetish)?.emoji ?? "✨"}
                    </span>
                  }
                  onEdit={() => setEditingField("fetish")}
                />
              )}
            </div>
          )}

          {/* ── Character Tab ── */}
          {activeTab === "character" && (
            <div className="space-y-2 overflow-y-auto max-h-[380px] sm:max-h-[460px] pr-0.5">
              <div className="grid grid-cols-2 gap-2">
                <FlatCard label="Name" value={draft.name?.trim() || "Unnamed"} />
                <FlatCard
                  label="Personality"
                  value={personalityLabel || "Not set"}
                  emoji={personalityChips[0]?.emoji}
                  onEdit={summaryEditingDisabled ? undefined : () => setEditingField("personality")}
                />
                <FlatCard
                  label="Relationship"
                  value={relationshipLabel || "Not set"}
                  emoji={relationshipOpt?.emoji}
                  onEdit={summaryEditingDisabled ? undefined : () => setEditingField("relationship")}
                />
                <FlatCard
                  label="Occupation"
                  value={occupationVal || "Not set"}
                  emoji={occupationOpt?.emoji}
                  onEdit={summaryEditingDisabled ? undefined : () => setEditingField("occupation")}
                />
                <FlatCard
                  label="Voice"
                  value={selectedVoiceName ?? "Not set"}
                  onEdit={summaryEditingDisabled ? undefined : () => setEditingField("voice")}
                  className="col-span-2 min-h-[72px] bg-gradient-to-br from-primary/10 via-transparent to-violet-500/10"
                />
                {hobbyVal && (
                  <FlatCard
                    label="Hobby"
                    value={hobbyVal}
                    emoji={hobbyOpt?.emoji}
                    onEdit={summaryEditingDisabled ? undefined : () => setEditingField("hobby")}
                  />
                )}
                {fetishVal && (
                  <FlatCard
                    label="Kinks"
                    value={fetishVal}
                    emoji={fetishOpt?.emoji}
                    onEdit={summaryEditingDisabled ? undefined : () => setEditingField("fetish")}
                  />
                )}
                {!readOnly && (
                  <FlatCard label="Visibility" value={(draft.isPublic ?? true) ? "Public" : "Private"} />
                )}
              </div>

              {/* Long-form details */}
              {!readOnly && draft.description?.trim() && (
                <DetailBlock label="Description">
                  <p className="text-sm text-foreground/70 leading-relaxed line-clamp-3">{draft.description}</p>
                </DetailBlock>
              )}
              {!readOnly && draft.backstory?.trim() && (
                <DetailBlock label="Definition">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {draft.backstory.length > 240 ? draft.backstory.slice(0, 240) + "…" : draft.backstory}
                  </p>
                </DetailBlock>
              )}
              {!readOnly && draft.greeting?.trim() && (
                <DetailBlock label="Opening Line">
                  <p className="text-sm text-foreground/60 leading-relaxed italic line-clamp-2">&ldquo;{draft.greeting}&rdquo;</p>
                </DetailBlock>
              )}
              {!readOnly && draft.scenario?.trim() && (
                <DetailBlock label="Scenario">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{draft.scenario}</p>
                </DetailBlock>
              )}
              {!readOnly && draft.customPhysicalDetails?.trim() && (
                <DetailBlock label="Custom Physical">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{draft.customPhysicalDetails}</p>
                </DetailBlock>
              )}
              {!readOnly && draft.additionalPersonalityDetails?.trim() && (
                <DetailBlock label="Personality Notes">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{draft.additionalPersonalityDetails}</p>
                </DetailBlock>
              )}
              {!readOnly && draft.extraDetails?.trim() && (
                <DetailBlock label="Extra Details">
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{draft.extraDetails}</p>
                </DetailBlock>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
